import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { PageLoading } from "../components/ui/PageLoading";
import { EmptyState } from "../components/ui/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Users, UserCog, Shield, Clock } from "lucide-react";
import { fetchUsers, fetchUserStats } from "../store/actions/userActions";

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const statMeta = [
  { nameKey: "users.totalUsers", key: "totalUsers", change: "", icon: Users },
  { nameKey: "users.activeUsers", key: "activeUsers", change: "", icon: UserCog },
  { nameKey: "users.totalRoles", key: "totalRoles", change: "", icon: Shield },
  { nameKey: "users.activeSessions", key: "activeSessions", change: "", icon: Clock },
];

const UsersPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { users = [], stats, loading, error } = useSelector((state) => state.user);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchUserStats());
  }, [dispatch]);

  const statValues = statMeta.map((s) => ({
    ...s,
    value: stats != null && s.key in stats ? String(stats[s.key]) : loading ? "…" : "—",
  }));

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("users.title")}
        sub={t("users.subtitle")}
        actions={
          <Button>
            <Shield style={{ height: "16px", width: "16px", marginRight: "8px" }} />
            {t("users.manageRoles")}
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("users.overview")}</TabsTrigger>
          <TabsTrigger value="users">{t("users.usersTab")}</TabsTrigger>
          <TabsTrigger value="roles">{t("users.rolesAndPermissions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }}>
            <StatsGrid>
              {statValues.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card key={stat.nameKey} style={{ padding: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <p style={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", margin: 0 }}>
                          {t(stat.nameKey)}
                        </p>
                        <p style={{ margin: "8px 0 0", fontSize: "30px", fontWeight: 600, color: "#111827" }}>
                          {stat.value}
                        </p>
                      </div>
                      <div
                        style={{
                          borderRadius: "9999px",
                          padding: "12px",
                          background: "rgba(155, 135, 245, 0.1)",
                        }}
                      >
                        <Icon style={{ height: "24px", width: "24px", color: "#7c3aed" }} />
                      </div>
                    </div>
                    {stat.change && (
                      <div style={{ marginTop: "16px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#16a34a" }}>
                          {stat.change}
                        </span>
                        <span style={{ fontSize: "14px", color: "#6b7280" }}> {t("users.fromLastMonth")}</span>
                      </div>
                    )}
                  </Card>
                );
              })}
            </StatsGrid>

            <Card style={{ padding: "24px", marginTop: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("users.quickActions")}</h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                <Button>
                  <Shield style={{ height: "16px", width: "16px", marginRight: "8px" }} />
                  {t("users.manageRoles")}
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card style={{ padding: "24px", marginTop: 24 }}>
            {error && (
              <p style={{ color: "#dc2626", marginBottom: "16px" }}>{error}</p>
            )}
            {loading && users.length === 0 ? (
              <PageLoading message={t("users.loadingUsers")} />
            ) : users.length === 0 ? (
              <EmptyState
                title={t("users.noUsersYet")}
                description={t("users.noUsersDesc")}
              />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("users.name")}</TableHead>
                      <TableHead>{t("users.email")}</TableHead>
                      <TableHead>{t("users.role")}</TableHead>
                      <TableHead>{t("users.plan")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u._id}>
                        <TableCell style={{ fontWeight: 600 }}>{u.fullName || "—"}</TableCell>
                        <TableCell>{u.email || "—"}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "9999px",
                              fontSize: "12px",
                              fontWeight: 600,
                              background: "rgba(155, 135, 245, 0.15)",
                              color: "#6d28d9",
                              textTransform: "capitalize",
                            }}
                          >
                            {u.role || "user"}
                          </span>
                        </TableCell>
                        <TableCell style={{ textTransform: "capitalize" }}>
                          {(u.plan || "—").replace("_", " ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card style={{ padding: "24px", marginTop: 24 }}>
            <p style={{ color: "#6b7280" }}>{t("users.rolesPlaceholder")}</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UsersPage;
